export interface ImageLimits {
  maxEdge: number;
  targetBytes: number;
}

export interface DecodedImage {
  width: number;
  height: number;
  source: unknown;
  close?: () => void;
}

export interface ImageCodec {
  decode(file: File): Promise<DecodedImage>;
  encode(
    image: DecodedImage,
    width: number,
    height: number,
    type: string,
    quality: number,
  ): Promise<Blob>;
}

export class ImageCompressionError extends Error {
  readonly code = 'IMAGE_TOO_LARGE';

  constructor() {
    super('image could not be compressed below the local size limit');
    this.name = 'ImageCompressionError';
  }
}

const DEFAULT_LIMITS: ImageLimits = { maxEdge: 1600, targetBytes: 800_000 };
const WEBP_QUALITIES = [0.86, 0.76, 0.66, 0.56] as const;
const JPEG_QUALITIES = [0.55, 0.45, 0.35] as const;

export async function compressImage(
  file: File,
  limits: ImageLimits = DEFAULT_LIMITS,
  codec: ImageCodec = createBrowserImageCodec(),
): Promise<Blob> {
  if (!Number.isFinite(limits.maxEdge) || !Number.isFinite(limits.targetBytes)
    || limits.maxEdge <= 0 || limits.targetBytes <= 0) {
    throw new TypeError('image limits must be positive finite numbers');
  }

  const image = await codec.decode(file);
  try {
    if (!Number.isFinite(image.width) || !Number.isFinite(image.height) || image.width <= 0 || image.height <= 0) {
      throw new TypeError('decoded image dimensions must be positive');
    }
    const scale = Math.min(1, limits.maxEdge / Math.max(image.width, image.height));
    let width = Math.max(1, Math.round(image.width * scale));
    let height = Math.max(1, Math.round(image.height * scale));

    for (const quality of WEBP_QUALITIES) {
      try {
        const blob = await codec.encode(image, width, height, 'image/webp', quality);
        if (blob.type === 'image/webp' && blob.size <= limits.targetBytes) return blob;
      } catch {
        break;
      }
    }
    while (true) {
      for (const quality of JPEG_QUALITIES) {
        try {
          const blob = await codec.encode(image, width, height, 'image/jpeg', quality);
          if (blob.type === 'image/jpeg' && blob.size <= limits.targetBytes) return blob;
        } catch {
          // A lower quality or smaller canvas may still be encodable.
        }
      }
      if (width === 1 && height === 1) break;
      width = Math.max(1, Math.floor(width * 0.8));
      height = Math.max(1, Math.floor(height * 0.8));
    }
    throw new ImageCompressionError();
  } finally {
    image.close?.();
  }
}

export function createBrowserImageCodec(): ImageCodec {
  return {
    decode: decodeInBrowser,
    encode: encodeInBrowser,
  };
}

async function decodeInBrowser(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    return {
      width: bitmap.width,
      height: bitmap.height,
      source: bitmap,
      close: () => bitmap.close(),
    };
  }
  if (typeof document === 'undefined' || typeof Image === 'undefined' || typeof URL.createObjectURL !== 'function') {
    throw new Error('this browser cannot decode local images');
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = objectUrl;
    await image.decode();
    return {
      width: image.naturalWidth,
      height: image.naturalHeight,
      source: image,
      close: () => URL.revokeObjectURL(objectUrl),
    };
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
}

async function encodeInBrowser(
  image: DecodedImage,
  width: number,
  height: number,
  type: string,
  quality: number,
): Promise<Blob> {
  let offscreenError: unknown;
  if (typeof OffscreenCanvas !== 'undefined') {
    try {
      const canvas = new OffscreenCanvas(width, height);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('2D canvas is unavailable');
      context.drawImage(image.source as CanvasImageSource, 0, 0, width, height);
      const blob = await canvas.convertToBlob({ type, quality });
      if (blob.type !== type) throw new Error(`${type} encoding is unavailable`);
      return blob;
    } catch (error) {
      offscreenError = error;
    }
  }

  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('2D canvas is unavailable');
    context.drawImage(image.source as CanvasImageSource, 0, 0, width, height);
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob || blob.type !== type) {
          reject(new Error(`${type} encoding is unavailable`));
          return;
        }
        resolve(blob);
      }, type, quality);
    });
  }

  if (offscreenError instanceof Error) throw offscreenError;
  throw new Error('canvas image encoding is unavailable');
}
